import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Container, Row } from 'react-bootstrap';
import {
  cropInfectionApi,
  getFormList,
  submitInfection
} from 'api/endpoints/CropInfection';
import { jwtDecode } from 'jwt-decode';
import { get, isEmpty } from 'lodash';
import useApi from 'hooks/useApi';
import DynamicForm from 'screens/common/dynamicForm';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import FalconCardHeader from 'components/common/FalconCardHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { farmReportApi } from 'api/endpoints/farm';
import { formSubmissionToast } from 'helpers/utils';
import { getTenantFromToken } from 'utils/routeUtils';
import useOnlineStatus from 'hooks/useOnlineStatus';
import offlineDBService from 'db/dexieDB';

function CreateFarm(props) {
  const tenant = getTenantFromToken();
  const tns_tenant = '03534add672340809e2c70314b1ce20e';
  const { id } = useParams();
  const farmer_id = id;
  const navigate = useNavigate();

  const [data, setData] = useState({});
  const getFormListApi = useApi(getFormList);
  const submitInfectionApi = useApi(submitInfection);
  const isOnline = useOnlineStatus();

  const [offlineMode, setOfflineMode] = useState(false);

  const fetchFormData = async () => {
    try {
      let formData;

      if (isOnline) {
        // Online - fetch from API
        let isEdit = false;
        const formResponse = await farmReportApi.getDynamicFarmForm(isEdit, id);
        formData = formResponse.data.results;
        setOfflineMode(false);
      } else {
        // Offline - fetch from IndexedDB
        console.log('Device is offline, fetching form from local DB');
        const formDef = await offlineDBService.getFormDefinition('farm-create');

        if (formDef && formDef.formData) {
          console.log('Form found in local DB:', formDef.formData);
          formData = formDef.formData;
          setOfflineMode(true);
        } else {
          console.error('No form definition found in local DB');
          setData({});
          toast.error(
            'No offline form data available. Please download when online.'
          );
          return;
        }
      }

      // At this point, we have our form data either from API or local DB

      // Now fetch all farmers from local DB to update the dropdown
      try {
        // Get all farmers from local DB
        const offlineFarmers = await offlineDBService.getAllFarmers();
        console.log(`Fetched ${offlineFarmers.length} farmers from offline DB`);

        // Process form fields to find the farmer dropdown
        if (formData && formData.fields) {
          // Find the farmer dropdown field
          const farmerFieldIndex = formData.fields.findIndex(
            field => field.id === 'farmer'
          );

          if (farmerFieldIndex !== -1) {
            // We found the farmer dropdown field
            const farmerField = formData.fields[farmerFieldIndex];

            // Store the existing options
            const existingOptions = Array.isArray(farmerField.options)
              ? [...farmerField.options]
              : [];
            console.log(
              `Found ${existingOptions.length} existing farmer options`
            );

            // Create a set of existing farmer IDs to avoid duplicates
            const existingFarmerIds = new Set(
              existingOptions.map(option => option.value)
            );

            // Create new options for offline farmers
            const offlineFarmerOptions = offlineFarmers
              .filter(farmer => {
                // Skip if this farmer is already in the existing options
                // This could happen if a farmer was synced and appears in both offline DB and API response
                return !existingFarmerIds.has(farmer.id);
              })
              .map(farmer => {
                // Format name based on available fields
                const firstName = farmer.first_name || '';
                const lastName = farmer.last_name || '';
                const fullName = `${firstName} ${lastName}`.trim();

                // Get phone number from whichever field it might be in
                const phone =
                  farmer.mobile_number ||
                  farmer.phone_number ||
                  farmer.phone ||
                  '';

                return {
                  // Use farmer.id as the value
                  value: farmer.id.toString(),
                  // Format label as "Name (Phone)" similar to existing options
                  label: `${fullName} (${phone})`
                };
              });

            console.log(
              `Adding ${offlineFarmerOptions.length} offline farmers to dropdown`
            );

            // Merge existing options with offline farmer options
            formData.fields[farmerFieldIndex].options = [
              ...existingOptions,
              ...offlineFarmerOptions
            ];

            console.log(
              `Updated farmer dropdown now has ${formData.fields[farmerFieldIndex].options.length} options`
            );
          } else {
            console.warn('Farmer dropdown field not found in form definition');
          }
        }
      } catch (farmersError) {
        console.error('Error fetching farmers from offline DB:', farmersError);
        // Continue with the original form data if we fail to add offline farmers
      }

      // Set the updated form data
      setData(formData);
    } catch (error) {
      console.error('Error fetching form data:', error);

      // Try to fall back to local DB even if error is not offline-related
      try {
        const formDef = await offlineDBService.getFormDefinition('farm-create');
        if (formDef && formDef.formData) {
          console.log('Falling back to local DB after API error');

          let formData = formDef.formData;

          // Try to add offline farmers to the dropdown
          try {
            const offlineFarmers = await offlineDBService.getAllFarmers();

            if (formData && formData.fields) {
              const farmerFieldIndex = formData.fields.findIndex(
                field => field.id === 'farmer'
              );

              if (farmerFieldIndex !== -1) {
                const farmerField = formData.fields[farmerFieldIndex];
                const existingOptions = Array.isArray(farmerField.options)
                  ? [...farmerField.options]
                  : [];
                const existingFarmerIds = new Set(
                  existingOptions.map(option => option.value)
                );

                const offlineFarmerOptions = offlineFarmers
                  .filter(farmer => !existingFarmerIds.has(farmer.id))
                  .map(farmer => {
                    const firstName = farmer.first_name || '';
                    const lastName = farmer.last_name || '';
                    const fullName = `${firstName} ${lastName}`.trim();
                    const phone =
                      farmer.mobile_number ||
                      farmer.phone_number ||
                      farmer.phone ||
                      '';

                    return {
                      value: farmer.id.toString(),
                      label: `${fullName} (${phone})`
                    };
                  });

                formData.fields[farmerFieldIndex].options = [
                  ...existingOptions,
                  ...offlineFarmerOptions
                ];
              }
            }
          } catch (farmersError) {
            console.error(
              'Error adding offline farmers during fallback:',
              farmersError
            );
          }

          setData(formData);
          setOfflineMode(true);
        } else {
          toast.error('Failed to load form. No offline data available.');
        }
      } catch (dbError) {
        console.error('Error fetching from local DB:', dbError);
        toast.error('Failed to load form data');
      }
    }
  };

  useEffect(() => {
    fetchFormData();
  }, [tns_tenant]);

  // Handle form submission - improved error handling
  const handleFormSubmit = async formData => {
    try {
      if (isOnline) {
        let formatedData = {
          ...formData,
          block: formData.block || [],
          district: formData.district || [],
          state: formData.state || '',
          village: formData.village || []
        };
        try {
          const response = await farmReportApi.submitFarm(
            farmer_id,
            formatedData
          );
          formSubmissionToast(response, toast, 'Farm');
          navigate(`/${tenant}/farmerReport/`);
        } catch (err) {
          console.error('Error submitting form:', err);
          // Show the API error directly without falling back to local storage
          formSubmissionToast(err, toast, 'Farm');
          return;
        }
      } else {
        // Offline - save to IndexedDB
        console.log('Device is offline, saving form submission locally');
        await offlineDBService.saveFarm(formData);
        toast.success('Farm data saved locally. Will sync when online.');
        navigate(`/offline-data/`);
      }
    } catch (err) {
      // This catch block will handle unexpected errors not related to API or offline storage
      console.error('Unexpected error in form submission:', err);

      if (!isOnline) {
        // Only attempt local storage as fallback if offline
        try {
          await offlineDBService.saveFarm(formData);
          toast.warning('Error occurred. Data saved locally for later sync.');
          navigate(`/${tenant}/farmerReport/`);
        } catch (dbError) {
          console.error('Error saving locally:', dbError);
          toast.error('Failed to save data locally. Please try again.');
        }
      } else {
        // If online and we reached this point, it's a truly unexpected error
        toast.error('An unexpected error occurred. Please try again.');
      }
    }
  };

  const header = (
    <div className="d-flex align-items-center">
      <div
        className="me-2 my-auto theme-control-toggle"
        onClick={() =>
          offlineMode
            ? navigate(`/offline-data`)
            : navigate(`/${tenant}/farmerReport`)
        }
      >
        <div className="theme-control-toggle-label">
          <FontAwesomeIcon icon={faArrowLeft} className="fs-0" />
        </div>
      </div>
      <span>Create Farm</span>
    </div>
  );

  console.log(data, 'datadatadatadata');

  return (
    <Container>
      <Row>
        <Card className="mb-3">
          <Card.Header>
            <FalconCardHeader
              title={header}
              className="ps-lg-4"
              titleTag="h5"
              titleClass="mb-0"
              light
            />
          </Card.Header>
          <Card.Body>
            {!isEmpty(data) && (
              <DynamicForm
                data={data}
                header={true}
                onSubmit={handleFormSubmit}
              />
            )}
          </Card.Body>
        </Card>
      </Row>
    </Container>
  );
}

CreateFarm.propTypes = {};

export default CreateFarm;
